import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { setupApiClient } from "@semo/api-contracts";
import App from "./App";
import { queryClient } from "./lib/queryClient";
import "@semo/shared-styles";
import "@semo/icons/tokens.css";

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !import.meta.env.DEV,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Ignore specific string-based errors
  ignoreErrors: [
    "Failed to execute 'removeChild' on 'Node'",
    "Acquiring an exclusive Navigator LockManager lock",
    "Network connection failed",
    "Request timeout",
    "Network Error",
  ],
  // Filter out noisy network errors
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Ignore Axios/Network errors with specific client error statuses
    if (error && typeof error === "object" && "response" in error) {
      const response = error.response;
      if (response && typeof response === "object" && "status" in response) {
        const status = response.status;
        if (
          typeof status === "number" &&
          [400, 401, 403, 404, 409, 429].includes(status)
        ) {
          return null;
        }
      }
    }

    return event;
  },
});

// Initialize API client with environment configuration
setupApiClient({
  baseURL: import.meta.env.VITE_API_URL ?? "/",
  withCredentials: true,
  apiKey: import.meta.env.VITE_SUPPLIER_API_KEY,
  headers: {
    "x-client-type": "vendor",
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
